<template>
  <EChart :option :selected-index @click:item="handleClickItem" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useDark, useLocalStorage } from '@vueuse/core'

import type { EChartOption } from 'echarts'

import { BLUR_DIGITAL_VALUES } from '@/const/storage'
import {
  BAR_CHART_NEGATIVE_COLOR_DARK,
  BAR_CHART_NEGATIVE_COLOR_LIGHT,
  BAR_CHART_SELECTED_NEGATIVE_COLOR_DARK,
  BAR_CHART_SELECTED_NEGATIVE_COLOR_LIGHT,
  BAR_CHART_SELECTED_NEGATIVE_SHADOW_COLOR_DARK,
  BAR_CHART_SELECTED_NEGATIVE_SHADOW_COLOR_LIGHT,
} from './utils/const'

import EChart from './EChart.vue'

type Props = {
  data: [string, number][]

  valueFormatter?: (data: number, fractionDigits?: number) => string
}

type Emits = {
  'click:bar': [index: number, name: string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const selectedIndex = defineModel<number>('index')

const isDark = useDark()

const blurDigitalValues = useLocalStorage(BLUR_DIGITAL_VALUES, false)

const negativeColor = computed(() =>
  isDark.value ? BAR_CHART_NEGATIVE_COLOR_DARK : BAR_CHART_NEGATIVE_COLOR_LIGHT,
)
const negativeSelectedColor = computed(() =>
  isDark.value
    ? BAR_CHART_SELECTED_NEGATIVE_COLOR_DARK
    : BAR_CHART_SELECTED_NEGATIVE_COLOR_LIGHT,
)

const category = computed(() => props.data.map(item => item[0]))

const data = computed(() =>
  props.data.map(item =>
    item[1] < 0
      ? {
        value: item[1],
        itemStyle: {
          color: negativeColor.value,
        },
        select: {
          itemStyle: {
            color: negativeSelectedColor.value,
            shadowColor: isDark.value
              ? BAR_CHART_SELECTED_NEGATIVE_SHADOW_COLOR_DARK
              : BAR_CHART_SELECTED_NEGATIVE_SHADOW_COLOR_LIGHT,
          },
        },
      }
      : item[1],
  ),
)

const option = computed(() => {
  return {
    tooltip: {
      formatter: (
        params: EChartOption.Tooltip.Format | EChartOption.Tooltip.Format[],
      ) => {
        const { color, name, value } = params as EChartOption.Tooltip.Format
        const result = props.valueFormatter?.(Number(value)) || value
        return `<div style="color:${color}">${name}</div><span class="number-node">${result}</span>`
      },
    },
    xAxis: {
      data: category.value,
      axisLabel: { interval: 0, rotate: 30 },
    },
    yAxis: {
      axisLabel: {
        formatter: (value: number) =>
          blurDigitalValues.value
            ? '? ??? ???'
            : props.valueFormatter?.(value, 0) || value,
      },
    },
    series: [
      {
        type: 'bar',
        selectedMode: true,
        data: data.value,
      },
    ],
    grid: {
      left: 0,
      right: 0,
      bottom: 0,
      height: '97%',
    },
  }
})

const handleClickItem = (index: number, name: string) => {
  selectedIndex.value = index
  emit('click:bar', index, name)
}
</script>
